// File-intake helpers shared by the batch uploader's folder picker, drag-drop,
// and file picker. Pure and dependency-light so they can be unit-tested in Node
// without a browser or import.meta. The uploader passes its own size limits in.
import { SUPPORTED_EXTENSIONS, RAW_OR_PRO_EXTENSIONS } from './supportedExtensions.js';

const SUPPORTED_SET = new Set(SUPPORTED_EXTENSIONS.map((e) => e.toLowerCase()));

// Exact-name OS/system junk (Windows + macOS + Linux) that should never upload.
const OS_JUNK_NAMES = new Set([
  'thumbs.db', 'desktop.ini', 'ehthumbs.db', 'ehthumbs_vista.db',
  '.ds_store', '.localized', 'icon\r', 'icon\r\r', '.directory', '.nomedia',
]);
// Path segments (directories) whose entire contents are junk.
const OS_JUNK_DIRS = new Set([
  '__macosx', '.spotlight-v100', '.trashes', '.fseventsd', '.temporaryitems',
  '.documentrevisions-v100', '$recycle.bin', 'system volume information', '.apdisk',
]);

/** basename of a path, handling both / and \ separators. */
export function baseName(name = '') {
  return String(name).split(/[\\/]/).filter(Boolean).pop() || '';
}

/**
 * Lowercased extension INCLUDING the dot (e.g. ".cr2"), or '' when there is no
 * real extension. A leading dot (hidden file) or no dot → '' (never treat a
 * whole folder/file name like "2026.Smith-Wedding" as an extension by itself —
 * that only matters if the trailing token happens to be a known photo ext).
 */
export function getExtension(name = '') {
  const base = baseName(name);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return ''; // -1 = no dot; 0 = hidden dotfile → no extension
  return base.slice(dot).toLowerCase();
}

/**
 * True for OS/system files or files inside a system directory. These are
 * skipped SILENTLY (not shown to the user as "blocked") — a photographer
 * dragging a shoot folder shouldn't see .DS_Store / Thumbs.db warnings.
 */
export function isOsJunk(file) {
  const name = typeof file === 'string' ? file : (file?.name || '');
  const relPath = typeof file === 'string' ? '' : (file?.webkitRelativePath || '');
  const base = baseName(name).toLowerCase();
  if (!base) return true;
  if (OS_JUNK_NAMES.has(base)) return true;
  if (base.startsWith('._')) return true; // macOS AppleDouble resource forks
  if (base.startsWith('.')) return true;  // any hidden dotfile
  // Any ancestor directory that is a known junk container.
  const segs = String(relPath).toLowerCase().split(/[\\/]/).filter(Boolean);
  for (const seg of segs.slice(0, -1)) {
    if (OS_JUNK_DIRS.has(seg)) return true;
  }
  return false;
}

/** Accepted if it has a supported photo extension, or a real image/* MIME type. */
export function isSupported(file, ext = getExtension(file?.name || '')) {
  if (ext && SUPPORTED_SET.has(ext)) return true;
  const mime = String(file?.type || '');
  return mime.startsWith('image/');
}

/**
 * A RAW/HEIC upload that's actually a tiny preview/placeholder (some export
 * tools or cloud-synced originals hand the browser a stub) — better to skip
 * than to store a broken "RAW".
 */
export function isSuspiciousRawPlaceholder(file, ext = getExtension(file?.name || ''), minRawBytes = 4096) {
  if (!file || !RAW_OR_PRO_EXTENSIONS.has(ext)) return false;
  const bytes = Number(file.size || 0);
  if (bytes > 0 && bytes < minRawBytes) return true;
  const mime = String(file.type || '').toLowerCase();
  if (!mime) return false;
  return mime.includes('svg') || mime.includes('xml') || mime === 'text/html' || mime === 'text/plain';
}

/**
 * Sort an incoming list of File objects into buckets. OS junk is dropped
 * silently into `junk`; everything else is validated exactly like the previous
 * inline logic.
 * @returns {{valid:File[], invalid:File[], oversize:File[], suspicious:File[], junk:File[]}}
 */
export function classifyFiles(fileList, { maxBytes = 0, minRawBytes = 4096 } = {}) {
  const valid = [], invalid = [], oversize = [], suspicious = [], junk = [];
  for (const f of Array.from(fileList || [])) {
    if (isOsJunk(f)) { junk.push(f); continue; }
    const ext = getExtension(f.name || '');
    if (!isSupported(f, ext)) { invalid.push(f); continue; }
    if (isSuspiciousRawPlaceholder(f, ext, minRawBytes)) { suspicious.push(f); continue; }
    if (maxBytes && Number(f.size || 0) > maxBytes) { oversize.push(f); continue; }
    valid.push(f);
  }
  return { valid, invalid, oversize, suspicious, junk };
}

/**
 * Recurse an array of webkit FileSystemEntry objects (from
 * DataTransferItem.webkitGetAsEntry(), captured synchronously in the drop
 * handler) into a flat list of File objects. Each file gets a
 * webkitRelativePath so callers can group by top-level folder (album-per-shoot).
 */
export async function readEntriesToFiles(entries) {
  const out = [];
  async function walk(entry, prefix) {
    if (!entry) return;
    if (entry.isFile) {
      const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
      const rel = prefix + entry.name;
      try { Object.defineProperty(file, 'webkitRelativePath', { value: rel, configurable: true }); } catch { /* read-only in some engines */ }
      out.push(file);
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      // readEntries returns at most 100 per call — loop until empty.
      let batch;
      do {
        batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        for (const child of batch) await walk(child, `${prefix}${entry.name}/`);
      } while (batch.length > 0);
    }
  }
  for (const entry of entries || []) await walk(entry, '');
  return out;
}
